import {
  Options,
  useRequestFormStore,
} from 'src/features/requests/request-form/use-request-form-store';
import { clearRequestFormDraft } from 'src/features/requests/request-form/clear-request-form-draft';
import { useConfirm } from 'src/components/ConfirmModal';
import { useShallow } from 'zustand/react/shallow';
import { useVideoVolumeModalStore } from 'src/features/company/use-video-volume-modal-store';
import { useBrandSelection } from 'src/providers/BrandSelectionProvider/brand-selection-context';
import * as brandClient from 'src/lib/services/api/brand-api/brand-client';
import { BrandResponse } from 'src/lib/services/api/brand-api/brand-client.types';

const useOpenRequestModal = () => {
  const { selectedBrand, selectedBrandHasVideoCount } = useBrandSelection();
  const { setIsModalOpen: setVideoVolumeModalOpen } = useVideoVolumeModalStore(
    useShallow(({ setIsModalOpen }) => ({ setIsModalOpen })),
  );

  const { isMinimized, openModal, reset } = useRequestFormStore(
    useShallow(({ isMinimized, openModal, reset }) => ({
      isMinimized,
      openModal,
      reset,
    })),
  );
  const { confirm } = useConfirm();
  const openRequestModal = async (
    requestId?: string,
    options?: Options,
    onAfterTrialCheck?: () => boolean | void,
  ) => {
    // Gate with Video Volume modal regardless of subscription status
    // Always show modal if the brand hasn't answered the monthly video count question
    if (selectedBrand) {
      // If we know the brand doesn't have video count, show the modal
      if (selectedBrandHasVideoCount === false) {
        setVideoVolumeModalOpen(true);
        return;
      }

      // If unknown state (null), fallback to API call for safety
      if (selectedBrandHasVideoCount === null) {
        try {
          const fullBrandData: BrandResponse = await brandClient.get(selectedBrand.id);

          if (!fullBrandData.brand_metadata?.monthly_video_count) {
            setVideoVolumeModalOpen(true);
            return;
          }
        } catch (error) {
          // If API call fails, show the modal as safer default
          setVideoVolumeModalOpen(true);
          return;
        }
      }

      // If selectedBrandHasVideoCount === true, continue to Create Request Modal
    }

    if (onAfterTrialCheck) {
      const shouldStop = onAfterTrialCheck();
      if (shouldStop) {
        return; // Stop processing if the callback returns true
      }
    }

    if (!isMinimized) {
      clearRequestFormDraft(requestId);
      // Skip style selection - go straight to media upload (step 2) for new requests
      const modalOptions = {
        ...options,
        currentStep: options?.selectedTemplate ? options.currentStep : 2,
        flowStep: options?.selectedTemplate ? undefined : 'media' as const,
        selectedStyle: options?.selectedTemplate ? undefined : 'custom' as const,
      };
      openModal(modalOptions);
      return;
    }

    const confirmed = await confirm('', {
      modal_header: {
        content: 'Draft request in progress',
      },
      modal_body: {
        content:
          'Looks like you already have a request form open. Opening a new one will discard your draft and any changes you’ve made won’t be saved.',
      },
      confirm: {
        text: 'Discard draft',
      },
      cancel: {
        text: 'Keep editing',
      },
    });

    // modal closed - cancel action and do nothing
    if (confirmed === null) {
      return;
    }

    if (confirmed) {
      reset();
      clearRequestFormDraft(requestId);
      // Skip style selection - go straight to media upload (step 2) for new requests
      const modalOptions = {
        ...options,
        currentStep: options?.selectedTemplate ? options.currentStep : 2,
        flowStep: options?.selectedTemplate ? undefined : 'media' as const,
        selectedStyle: options?.selectedTemplate ? undefined : 'custom' as const,
      };
      openModal(modalOptions);
      return;
    }

    openModal();
  };

  return { openRequestModal };
};

export { useOpenRequestModal };
